import React from 'react';
import { FormControl, MenuItem, Select, Typography, useTheme, SelectChangeEvent } from '@mui/material';
import { ExpandMoreOutlined } from '@mui/icons-material';

interface SelectOption {
  _id: string;
  projectName: string;
}

interface SelectFieldProps {
  value: string;
  onChange: (event: SelectChangeEvent<string>, selectedOption?: SelectOption) => void;
  options: SelectOption[] | string; // Handle both array and string
  placeholder?: string;
  id?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({ value, onChange, options, placeholder, id }) => {
  const theme = useTheme();

  const renderOptions = () => {
    if (typeof options === 'string') {
      // Render a single MenuItem if options is a string
      return (
        <MenuItem value={options}>
          <Typography>{options}</Typography>
        </MenuItem>
      );
    }

    // Render multiple MenuItems if options is an array
    return options?.map((option) => (
      <MenuItem key={option._id} value={option._id}>
        {option.projectName}
      </MenuItem>
    ));
  };

  return (
    <FormControl fullWidth required>
      <Select
        id={id}
        value={value}
        onChange={(event: SelectChangeEvent<string>) => {
          const selectedValue = event.target.value as string;
          const selectedOption = typeof options !== 'string' ? options.find(
            (option) => option._id === selectedValue
          ) : undefined;
          onChange(event, selectedOption);
        }}
        IconComponent={ExpandMoreOutlined}
        sx={{
          width: "100%",
          border: "none",
          boxShadow: "none",
          "& .MuiSelect-select": {
            padding: theme.spacing(0.8, 0, 0.8, 0),
          },
          "&:hover, & fieldset, & fieldset:hover": {
            border: "none",
            boxShadow: "none",
          },
        }}
        renderValue={(selected) => {
          if (typeof options === 'string') {
            return selected === options ? options : '';
          }
          if (selected === '' && placeholder) {
            return (
              <Typography
                sx={{
                  opacity: "0.6",
                  ml: 1,
                  color: "#5A525BB2",
                  fontFamily: "Buenos Aires",
                  fontSize: "16px",
                }}
              >
                {placeholder}
              </Typography>
            );
          }
          if (typeof options !== 'string') {
            const selectedOption = options.find(
              (option) => option._id === selected
            );
            return selectedOption ? selectedOption.projectName : '';
          }
          return '';
        }}
        fullWidth
      >
        {placeholder && (
          <MenuItem value="">
            <Typography>{placeholder}</Typography>
          </MenuItem>
        )}
        {renderOptions()}
      </Select>
    </FormControl>
  );
};
